import * as SentryOriginal from '@sentry/react';
import React, { useEffect } from 'react';
import { Routes as OriginalRoutes } from '@gogame/screens';
import SentryProvider from './SentryProvider';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';

import { ChartErrorBoundary } from './ChartErrorBoundary';
import { SidePageErrorBoundary } from './SidePageErrorBoundary';
import { RouterErrorBoundary } from './RouterErrorBoundary';
import captureError from './captureError';
import { FormFieldErrorBoundary } from './FormFieldErrorBoundary';
import { FormErrorBoundary } from './FormErrorBoundary';

const normalizeUrl = (url) => {
  return url.replace(/\/\d+(\?|$)/g, '/:id$1').replace(/\/\*$/g, '');
};
const isQAUser = (user) => {
  return user?.position === 'autoTestUser';
};

export const init = ({
  dsn,
  environment,
  release,
  sampleRate,
  tracesSampleRate,
  tracePropagationTargets,
}) => {
  SentryOriginal.init({
    dsn: dsn,
    environment: environment,
    release: release,
    ignoreErrors: [
      /Remote API/i,
      /Network Error/i,
      /Fetch Error/i,
      /AxiosError/i,
      /BrowserAuthError/i,
      /ResizeObserver/i,
      /ChunkLoadError/i,
    ],
    beforeSend(event, hint) {
      if (isQAUser(event?.user)) {
        return null;
      }

      if (event.request?.url) {
        event.request.url = normalizeUrl(event.request.url);
      }
      if (event.exception && event.exception.values) {
        const errorMessage = event.exception.values[0].value;
        if (
          errorMessage?.includes('Axios') ||
          errorMessage?.includes('Request') ||
          errorMessage?.includes('fetch') ||
          errorMessage?.includes('Correlation') ||
          errorMessage?.includes('Validation')
        ) {
          return null;
        }
      }
      if (hint.originalException instanceof Error) {
        event.extra = {
          ...(event.extra || {}),
          message: hint.originalException.message,
          stack: hint.originalException.stack,
        };
      }

      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console') return null;
      if (breadcrumb.category === 'sentry.event') return null;
      if (breadcrumb.category === 'sentry.transaction') return null;

      return breadcrumb;
    },
    sampleRate: sampleRate,
    tracesSampleRate: tracesSampleRate,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: tracePropagationTargets,
    integrations: [
      new SentryOriginal.breadcrumbsIntegration({
        console: false,
        dom: false,
        xhr: false,
        fetch: false,
      }),
      SentryOriginal.replayIntegration({
        blockSelector: null,
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
      SentryOriginal.replayCanvasIntegration(),
      SentryOriginal.reactRouterV6BrowserTracingIntegration(
        useEffect,
        useLocation,
        useNavigationType,
        useNavigate,
        createRoutesFromChildren,
        matchRoutes
      ),
      SentryOriginal.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),
    ],
  });
};
const sentryCreateBrowserRouter =
  SentryOriginal.wrapCreateBrowserRouterV6(createBrowserRouter);

const Routes = SentryOriginal.withSentryReactRouterV6Routing(OriginalRoutes);

const addBreadcrumb = (params) => {
  SentryOriginal.addBreadcrumb({
    level: 'info',
    timestamp: new Date().toISOString(),
    ...params,
  });
};

export const Sentry = {
  Routes,
  sentryCreateBrowserRouter,
  init,
  setTag: SentryOriginal.setTag,
  setUser: SentryOriginal.setUser,
  captureError,
  Provider: SentryProvider,
  ChartErrorBoundary,
  SidePageErrorBoundary,
  RouterErrorBoundary,
  FormFieldErrorBoundary,
  FormErrorBoundary,
  addBreadcrumb,
};
